import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  padding: '80px 0',
  position: 'relative',
  overflow: 'hidden',
  // Responsive padding
  [theme.breakpoints.down('xl')]: {
    padding: '70px 0'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '60px 0'
  },
  [theme.breakpoints.down('md')]: {
    padding: '50px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 0'
  },
  '@media (max-width: 480px)': {
    padding: '30px 0'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '100px 0'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#B4A082',
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  fontSize: '0.9rem',
  marginBottom: '24px',
  // Responsive font sizes
  [theme.breakpoints.down('lg')]: {
    fontSize: '0.85rem',
    marginBottom: '20px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '0.8rem',
    marginBottom: '18px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginBottom: '16px'
  },
  '@media (max-width: 480px)': {
    fontSize: '0.7rem',
    marginBottom: '14px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1rem',
    marginBottom: '28px'
  }
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  color: '#2c2c2c',
  fontWeight: 300,
  lineHeight: 1.2,
  marginBottom: '40px',
  fontSize: '1.8rem',
  letterSpacing: '1px',
  fontFamily: '"Playfair Display", serif',
  // Responsive font sizes
  [theme.breakpoints.up('sm')]: {
    fontSize: '2rem'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2.5rem',
    marginBottom: '35px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '3rem',
    marginBottom: '38px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '3.5rem',
    marginBottom: '40px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '4rem',
    marginBottom: '48px'
  },
  // Small mobile
  '@media (max-width: 480px)': {
    fontSize: '1.5rem',
    marginBottom: '24px',
    lineHeight: 1.3
  }
}));

const ContentText = styled(Typography)(({ theme }) => ({
  color: '#666',
  lineHeight: 1.8,
  fontSize: '1.1rem',
  marginBottom: '32px',
  maxWidth: '600px',
  // Responsive font sizes
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.05rem',
    marginBottom: '28px',
    maxWidth: '550px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    marginBottom: '24px',
    maxWidth: '500px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
    marginBottom: '20px',
    maxWidth: '100%',
    lineHeight: 1.7
  },
  '@media (max-width: 480px)': {
    fontSize: '0.9rem',
    marginBottom: '16px',
    lineHeight: 1.6
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.2rem',
    marginBottom: '36px',
    maxWidth: '700px'
  }
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#455b40',
  color: 'white',
  fontSize: '1rem',
  fontWeight: 500,
  borderRadius: '50px',
  textTransform: 'none',
  letterSpacing: '0.5px',
  boxShadow: '0 8px 25px rgba(166, 144, 128, 0.3)',
  transition: 'all 0.3s ease',
  margin: '0 15px 15px 0',
  padding: '12px 30px',
  minWidth: '180px',
  // Responsive sizing
  [theme.breakpoints.down('lg')]: {
    padding: '11px 28px',
    fontSize: '0.95rem',
    minWidth: '170px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '10px 26px',
    fontSize: '0.9rem',
    minWidth: '160px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '9px 24px',
    fontSize: '0.85rem',
    minWidth: '150px',
    margin: '0 10px 10px 0'
  },
  '@media (max-width: 480px)': {
    padding: '8px 20px',
    fontSize: '0.8rem',
    minWidth: '140px',
    margin: '0 8px 8px 0'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '14px 36px',
    fontSize: '1.1rem',
    minWidth: '200px'
  },
  '&:hover': {
    backgroundColor: '#657c61',
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 35px rgba(166, 144, 128, 0.4)',
  },
}));

const Familia: React.FC = () => {
  const navigate = useNavigate();

  const handleConocenosClick = () => {
    navigate('/quienes-somos');
  };

  return (
    <SectionContainer>
      <Box sx={{ 
        maxWidth: { 
          xs: '95%', 
          sm: '90%', 
          md: '85%', 
          lg: '85%', 
          xl: '85%' 
        },
        '@media (min-width: 1920px)': {
          maxWidth: '80%'
        },
        margin: '0 auto',
        px: { xs: 2, sm: 2, md: 0 }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'stretch', 
          minHeight: { 
            xs: '400px', 
            sm: '450px', 
            md: '500px', 
            lg: '550px' 
          },
          '@media (min-width: 1920px)': {
            minHeight: '600px'
          },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 0,
          backgroundColor: 'white',
          borderRadius: { xs: '8px', sm: '10px', md: '10px' },
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
        }}>
          {/* Left Content */}
          <Box sx={{ 
            flex: 1, 
            backgroundColor: 'white',
            padding: { 
              xs: '30px 20px', 
              sm: '35px 25px', 
              md: '50px 40px', 
              lg: '55px 45px', 
              xl: '60px 50px' 
            },
            '@media (min-width: 1920px)': {
              padding: '70px 60px'
            },
            '@media (max-width: 480px)': {
              padding: '25px 18px'
            },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            maxWidth: { md: '55%' }
          }}>
            <MainHeading sx={{ 
              mb: { xs: 2, sm: 2.5, md: 3 },
              fontSize: { 
                xs: '1.5rem', 
                sm: '1.8rem', 
                md: '2.2rem', 
                lg: '2.5rem', 
                xl: '2.8rem' 
              },
              '@media (min-width: 1920px)': {
                fontSize: '3.2rem'
              },
              '@media (max-width: 480px)': {
                fontSize: '1.3rem'
              }
            }}>
              Ekkofood: una familia comprometida con la salud y la tierra
            </MainHeading>
            
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ 
                color: '#666',
                lineHeight: 1.8,
                fontSize: { 
                  xs: '0.85rem', 
                  sm: '0.9rem', 
                  md: '0.95rem', 
                  lg: '1rem' 
                },
                '@media (min-width: 1920px)': {
                  fontSize: '1.1rem'
                },
                '@media (max-width: 480px)': {
                  fontSize: '0.8rem',
                  lineHeight: 1.6
                },
                mb: { xs: 2, sm: 2.5, md: 3 }
              }}>
                Ekkofood es una iniciativa familiar que no solo cultiva alimentos ecológicos, sino que, junto a expertos nacionales e internacionales, organiza cursos, talleres y programas de formación en agricultura orgánica y regenerativa.
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ 
                  color: '#B4A082', 
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  '@media (min-width: 1920px)': {
                    fontSize: '1.3rem'
                  },
                  mr: { xs: 1.5, sm: 2 } 
                }}>→</Typography>
                <Typography sx={{ 
                  color: '#666',
                  lineHeight: 1.8,
                  fontSize: { 
                    xs: '0.85rem', 
                    sm: '0.9rem', 
                    md: '0.95rem', 
                    lg: '1rem' 
                  },
                  '@media (min-width: 1920px)': {
                    fontSize: '1.1rem'
                  },
                  '@media (max-width: 480px)': {
                    fontSize: '0.8rem',
                    lineHeight: 1.6
                  }
                }}>
                  Estamos convencidos de que la salud está directamente ligada a lo que comemos. Por eso, en Ekkofood nos enfocamos en cultivar suelos saludables.
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ 
                  color: '#B4A082', 
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  '@media (min-width: 1920px)': {
                    fontSize: '1.3rem'
                  },
                  mr: { xs: 1.5, sm: 2 } 
                }}>→</Typography>
                <Typography sx={{ 
                  color: '#666',
                  lineHeight: 1.8,
                  fontSize: { 
                    xs: '0.85rem', 
                    sm: '0.9rem', 
                    md: '0.95rem', 
                    lg: '1rem' 
                  },
                  '@media (min-width: 1920px)': {
                    fontSize: '1.1rem'
                  },
                  '@media (max-width: 480px)': {
                    fontSize: '0.8rem',
                    lineHeight: 1.6
                  }
                }}>
                  Sin un suelo vivo, no pueden crecer plantas ni alimentos que realmente nutran.
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                <Typography sx={{ 
                  color: '#B4A082', 
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  '@media (min-width: 1920px)': {
                    fontSize: '1.3rem'
                  },
                  mr: { xs: 1.5, sm: 2 } 
                }}>→</Typography>
                <Typography sx={{ 
                  color: '#666',
                  lineHeight: 1.8,
                  fontSize: { 
                    xs: '0.85rem', 
                    sm: '0.9rem', 
                    md: '0.95rem', 
                    lg: '1rem' 
                  },
                  '@media (min-width: 1920px)': {
                    fontSize: '1.1rem'
                  },
                  '@media (max-width: 480px)': {
                    fontSize: '0.8rem',
                    lineHeight: 1.6
                  }
                }}>
                  Nuestro compromiso se centra en apoyar a agricultores, técnicos y organizaciones a producir de manera sostenible.
                </Typography>
              </Box>
            </Box>

            <Box>
              <CTAButton onClick={handleConocenosClick}>
                Conócenos más
              </CTAButton>
            </Box>
          </Box>

          {/* Right Image */}
          <Box sx={{ 
            flex: 1,
            maxWidth: { md: '45%' },
            width: '100%',
            height: { 
              xs: '300px', 
              sm: '350px', 
              md: 'auto' 
            },
            '@media (max-width: 480px)': {
              height: '250px'
            },
            '@media (min-width: 1920px)': {
              height: 'auto'
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white'
          }}>
            <Box
              component="img"
              src="/familia.jpg"
              alt="Familia Ekkofood"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </Box>
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default Familia;